// frontend/src/hooks/useApiOperationsData.js
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, serviceApiKeysAPI } from '../services/api';
import toast from '../utils/toast';

const KEYS_ROOT = ['api-operations'];

export const useApiOperationsOverview = (days = 7, options = {}) => {
  return useQuery({
    queryKey: [...KEYS_ROOT, 'overview', { days }],
    queryFn: async () => {
      const response = await serviceApiKeysAPI.getOverview({ days });
      return response.data;
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useServiceApiKeys = (options = {}) => {
  return useQuery({
    queryKey: [...KEYS_ROOT, 'keys'],
    queryFn: async () => {
      const response = await serviceApiKeysAPI.list();
      return {
        keys: response.data?.keys || [],
        summary: response.data?.summary || {},
        retentionDays: response.data?.retention_days || 90,
      };
    },
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useServiceApiScopes = (options = {}) => {
  return useQuery({
    queryKey: [...KEYS_ROOT, 'scopes'],
    queryFn: async () => {
      const response = await serviceApiKeysAPI.listScopes();
      return response.data?.scopes || [];
    },
    staleTime: 30 * 60 * 1000,
    ...options,
  });
};

export const useApiRequestLogs = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [...KEYS_ROOT, 'request-logs', params],
    queryFn: async () => {
      const response = await serviceApiKeysAPI.getRequestLogs(params);
      return response.data;
    },
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    ...options,
  });
};

export const useApiAdminActivity = (params = {}, options = {}) => {
  return useQuery({
    queryKey: [...KEYS_ROOT, 'admin-activity', params],
    queryFn: async () => {
      const response = await serviceApiKeysAPI.getAdminActivity(params);
      return response.data;
    },
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    ...options,
  });
};

const invalidateApiOperations = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: KEYS_ROOT });
};

export const useCreateServiceApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => serviceApiKeysAPI.create(data),
    onSuccess: () => {
      invalidateApiOperations(queryClient);
      toast.success('API key generated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to generate API key');
    },
  });
};

export const useUpdateServiceApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => serviceApiKeysAPI.update(id, data),
    onSuccess: (_res, variables) => {
      invalidateApiOperations(queryClient);
      const next = variables?.data?.is_active;
      if (typeof next === 'boolean') {
        toast.success(next ? 'Key re-enabled' : 'Key disabled');
      } else {
        toast.success('Key updated');
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update key');
    },
  });
};

export const useRevokeServiceApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => serviceApiKeysAPI.revoke(id),
    onSuccess: () => {
      invalidateApiOperations(queryClient);
      toast.success('API key revoked permanently');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to revoke key');
    },
  });
};

export const useRotateServiceApiKey = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => serviceApiKeysAPI.rotate(id),
    onSuccess: () => {
      invalidateApiOperations(queryClient);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to rotate key');
    },
  });
};

/**
 * Configurable fetch for ServiceApiKeysTab when used from the asset-admin
 * portal (different apiBase). Keeps React Query caching scoped by base path.
 */
export const useConfigurableServiceApiKeys = (apiBase, options = {}) => {
  return useQuery({
    queryKey: [...KEYS_ROOT, 'keys', { apiBase }],
    queryFn: async () => {
      const [keysRes, scopesRes] = await Promise.all([
        api.get(`${apiBase}/`),
        api.get(`${apiBase}/scopes/`),
      ]);
      return {
        keys: keysRes.data?.keys || [],
        summary: keysRes.data?.summary || {},
        scopes: scopesRes.data?.scopes || [],
        categories: scopesRes.data?.categories || [],
        retentionDays: keysRes.data?.retention_days || 90,
      };
    },
    staleTime: 60 * 1000,
    ...options,
  });
};
